import find from "lodash/find";
import ProfileEditNav from "PFApp/profiles/edit/nav/profile_edit_nav";
import css from "PFApp/profiles/edit/profile_edit_page.module.scss";
import { Inline } from "PFComponents/containers";
import NotFoundComponent from "PFComponents/not_found/not_found";
import { Typography } from "PFComponents/typography";
import { getProfileName } from "PFCore/helpers/profile";
import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import { useCurrentAccount } from "PFCore/hooks/queries/account";
import { useMyAccountInfo } from "PFCore/hooks/queries/account/use_my_account_info";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { FeatureFlag, PermissionsGroup } from "PFCore/types";
import { Profile } from "PFTypes";
import { useRef } from "react";
import { useTranslation } from "react-i18next";

import { NavSection } from "./profile_edit_sections.types";
import { ThemeToggle } from "./theme_toggle";

type ProfileEditPageProps = {
  profile: Profile;
  section: string;
  sections: NavSection[];
  adminPage?: boolean;
  tip?: string;
  tipName?: string;
  permissionsGroup: PermissionsGroup;
  handleProfileUpdate: VoidFunction;
};

export const ProfileEditPage = ({
  profile,
  adminPage,
  permissionsGroup,
  section,
  sections,
  handleProfileUpdate
}: ProfileEditPageProps) => {
  const { t } = useTranslation("profiles");
  const isEnabled = useIsFeatureEnabled();
  const { data: currentProfile } = useCurrentProfile();
  const { data: currentAccount } = useCurrentAccount();
  const { data: myAccountInfo } = useMyAccountInfo();

  const overlayContainerRef = useRef<HTMLDivElement>(null);

  const getSectionComponent = () => {
    const sectionContent = find(sections, { section });
    if (
      !sectionContent ||
      (sectionContent.requireFeature && !isEnabled(sectionContent.requireFeature)) ||
      (sectionContent.isVisible && !sectionContent.isVisible())
    ) {
      return null;
    }
    return sectionContent ? sectionContent.component : null;
  };

  const Section = getSectionComponent();

  const headerTitle = adminPage
    ? t("edit.updateProfileName", { profileName: getProfileName(profile) })
    : t("edit.updateYourProfile");

  return (
    <div className={css.root}>
      <Inline spread alignItems="center" className={css.headerContainer} ref={overlayContainerRef}>
        <Typography variant="h2" className={css.header}>
          {headerTitle}
        </Typography>
        {isEnabled(FeatureFlag.DarkMode) && !adminPage && <ThemeToggle />}
      </Inline>
      <div className={css.navAndSection}>
        <div className={css.nav}>
          <ProfileEditNav
            currentSection={section}
            sections={sections}
            adminPage={adminPage}
            // to be migrated to use profile (The one on the store, not the model)
            profile={profile}
          />
        </div>
        <div className={css.section}>
          {Section && (
            <Section
              profile={profile}
              currentProfile={currentProfile}
              myAccountInfo={myAccountInfo}
              currentAccount={currentAccount}
              adminPage={adminPage}
              permissionsGroup={permissionsGroup}
              handleProfileUpdate={handleProfileUpdate}
              overlayContainerRef={overlayContainerRef}
            />
          )}
          {!Section && <NotFoundComponent message={t("edit.contentCouldntLoad")} />}
        </div>
      </div>
    </div>
  );
};
